<div class="partner-agreement-container">

  <h1>Partner Referral Agreement</h1>
  <p class="agreement-meta">
    <strong>Last Updated:</strong> May 2026<br>
    <strong>Parties:</strong> xoomtag llc, doing business as Chareety ("Chareety", "We", "Us") and the Partner ("You", "Partner")
  </p>

  <p>
    This Partner Referral Agreement ("Agreement") is a legally binding contract between you and
    xoomtag llc, doing business as Chareety, located in Bartlett, IL 60103.
    By clicking "I Accept" or by registering as a partner on the Chareety platform, you acknowledge
    that you have read, understood, and agree to be bound by the terms and conditions of this Agreement.
  </p>

  <!-- Table of Contents -->
  <div class="table-of-contents">
    <h2>Table of Contents</h2>
    <ol>
      <li><a (click)="scrollTo('section-1')">Definitions</a></li>
      <li><a (click)="scrollTo('section-2')">Partner Registration</a></li>
      <li><a (click)="scrollTo('section-3')">Referral Process</a></li>
      <li><a (click)="scrollTo('section-4')">Commission Structure</a></li>
      <li><a (click)="scrollTo('section-5')">On-Site Setup Fee</a></li>
      <li><a (click)="scrollTo('section-6')">Payment Terms</a></li>
      <li><a (click)="scrollTo('section-7')">Partner as Support Provider</a></li>
      <li><a (click)="scrollTo('section-8')">Intellectual Property and Branding</a></li>
      <li><a (click)="scrollTo('section-9')">Non-Compete and Non-Solicitation</a></li>
      <li><a (click)="scrollTo('section-10')">Independent Contractor</a></li>
      <li><a (click)="scrollTo('section-11')">Representations and Warranties</a></li>
      <li><a (click)="scrollTo('section-12')">Confidentiality</a></li>
      <li><a (click)="scrollTo('section-13')">Limitation of Liability</a></li>
      <li><a (click)="scrollTo('section-14')">Indemnification</a></li>
      <li><a (click)="scrollTo('section-15')">Term</a></li>
      <li><a (click)="scrollTo('section-16')">Termination</a></li>
      <li><a (click)="scrollTo('section-17')">Commission Forfeiture</a></li>
      <li><a (click)="scrollTo('section-18')">Governing Law and Disputes</a></li>
      <li><a (click)="scrollTo('section-19')">Modifications</a></li>
      <li><a (click)="scrollTo('section-20')">General Provisions</a></li>
    </ol>
  </div>

  <!-- Section 1: Definitions -->
  <h2 id="section-1">1. Definitions</h2>
  <p>The following terms shall have the meanings set forth below when used in this Agreement:</p>
  <table class="definitions-table">
    <thead>
      <tr>
        <th>Term</th>
        <th>Definition</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td><strong>"Partner" / "You"</strong></td>
        <td>The individual or entity registering as a referral partner and referring nonprofit organizations to the Chareety platform.</td>
      </tr>
      <tr>
        <td><strong>"Chareety" / "We"</strong></td>
        <td>xoomtag llc, doing business as Chareety, the operator of the Chareety platform.</td>
      </tr>
      <tr>
        <td><strong>"Referral"</strong></td>
        <td>An introduction of a prospective nonprofit organization to Chareety made by the Partner through the Partner portal.</td>
      </tr>
      <tr>
        <td><strong>"Converted Referral"</strong></td>
        <td>A Referral that completes the onboarding process and becomes an active Organization on the Chareety platform.</td>
      </tr>
      <tr>
        <td><strong>"Organization"</strong></td>
        <td>A nonprofit entity that has completed onboarding and is actively using the Chareety platform.</td>
      </tr>
      <tr>
        <td><strong>"Platform Revenue"</strong></td>
        <td>
          Fees collected by Chareety from an Organization, including digital donation fees, subscription fees,
          offline record fees, and communication fees ($0.005 per email after 200 free/month).
          Platform Revenue expressly excludes:
          SIM connectivity fees, hardware purchases, on-site setup fees, and Stripe processing fees passed
          through to Stripe.
        </td>
      </tr>
    </tbody>
  </table>

  <!-- Section 2: Partner Registration -->
  <h2 id="section-2">2. Partner Registration</h2>
  <p>
    <strong>2.1</strong> Partners must register through the Partner portal at
    <strong>partners.chareety.com</strong> and provide accurate personal and/or business information
    as required during the registration process.
  </p>
  <p>
    <strong>2.2</strong> Chareety reserves the right to approve or reject any Partner application
    at its sole discretion, without obligation to provide a reason for rejection.
  </p>
  <p>
    <strong>2.3</strong> Upon approval, the Partner will receive a unique referral code that must be
    used when submitting referrals to ensure proper attribution.
  </p>
  <p>
    <strong>2.4</strong> The Partner is responsible for maintaining the accuracy of their account
    information and must promptly update any changes.
  </p>

  <!-- Section 3: Referral Process -->
  <h2 id="section-3">3. Referral Process</h2>
  <p>
    <strong>3.1</strong> The Partner submits referrals exclusively through the Partner portal.
    Referrals submitted through any other channel may not be honored.
  </p>
  <p>
    <strong>3.2</strong> Each Referral must be a new prospective organization that is not already
    in Chareety's sales pipeline, an existing Chareety customer, or previously referred by another Partner.
  </p>
  <p>
    <strong>3.3</strong> Attribution of a Referral to the Partner occurs upon submission through
    the Partner portal. Chareety will make reasonable efforts to verify that the Referral qualifies
    as new and will notify the Partner if the Referral is rejected due to duplication.
  </p>
  <p>
    <strong>3.4</strong> A Referral converts into a Converted Referral when the referred organization
    completes the Chareety onboarding process and becomes an active Organization on the platform.
  </p>

  <!-- Section 4: Commission Structure -->
  <h2 id="section-4">4. Commission Structure</h2>

  <h3>4.1 Commission Rates</h3>
  <table class="commission-table">
    <thead>
      <tr>
        <th>Period</th>
        <th>Commission Rate</th>
        <th>Duration</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>Months 1&ndash;3 from Organization onboarding date</td>
        <td>25% of the Organization's monthly Platform Revenue</td>
        <td>3 calendar months</td>
      </tr>
      <tr>
        <td>Month 4 onward</td>
        <td>15% of the Organization's monthly Platform Revenue</td>
        <td>Lifetime (as long as the Organization remains active)</td>
      </tr>
    </tbody>
  </table>

  <h3>4.2 Pro-Rated Calculations</h3>
  <p>
    The initial 3-month window at 25% is calculated as exactly 3 calendar months from the
    Organization's onboarding completion date. If the transition from 25% to 15% falls
    mid-month, that month's commission is blended proportionally based on the number of
    days at each rate.
  </p>
  <div class="example-box">
    <h4>Example: Blended Rate Calculation</h4>
    <p>
      An Organization completes onboarding on <strong>May 17th</strong>. The 25% rate applies from
      May 17th through August 17th (3 calendar months). Beginning August 18th, the rate drops to 15%.
    </p>
    <p>For the August payout (paid on September 1st), the commission is blended:</p>
    <ul>
      <li><strong>August 1&ndash;17 (17 days):</strong> 25% of Platform Revenue earned during those days</li>
      <li><strong>August 18&ndash;31 (14 days):</strong> 15% of Platform Revenue earned during those days</li>
    </ul>
    <p>
      The blended commission for August = (17/31 &times; 25%) + (14/31 &times; 15%) = approximately 20.52%
      of August's total Platform Revenue.
    </p>
  </div>

  <h3>4.3 Commission Basis</h3>
  <p>
    Commissions are calculated solely on Platform Revenue. The following table details what is
    included and excluded from Platform Revenue:
  </p>
  <table class="revenue-table">
    <thead>
      <tr>
        <th>Category</th>
        <th>Included / Excluded</th>
        <th>Rate / Amount</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>Digital donation fees</td>
        <td>Included</td>
        <td>0.5% + $0.10 per donation</td>
      </tr>
      <tr>
        <td>Subscription fees</td>
        <td>Included</td>
        <td>1% per recurring donation</td>
      </tr>
      <tr>
        <td>Offline record fees</td>
        <td>Included</td>
        <td>$0.10 per record (after free tier)</td>
      </tr>
      <tr>
        <td>Communication fees</td>
        <td>Included</td>
        <td>$0.005 per email after 200 free/month</td>
      </tr>
      <tr>
        <td>SIM connectivity fees</td>
        <td>Excluded</td>
        <td>$10/month per device</td>
      </tr>
      <tr>
        <td>Hardware purchases</td>
        <td>Excluded</td>
        <td>Varies</td>
      </tr>
      <tr>
        <td>On-site setup fees</td>
        <td>Excluded</td>
        <td>$100 per visit</td>
      </tr>
      <tr>
        <td>Stripe processing fees</td>
        <td>Excluded</td>
        <td>Passed through to Stripe</td>
      </tr>
    </tbody>
  </table>

  <h3>4.4 Lifetime Commissions</h3>
  <p>
    The Partner earns commissions on a Converted Referral for as long as the referred Organization
    remains an active customer on the Chareety platform, subject to the following rules:
  </p>
  <ul>
    <li>
      <strong>Same Partner re-referral:</strong> If an Organization leaves Chareety and the same
      Partner brings them back, the commission rate is 15% from day one. There is no reset of the
      3-month introductory window.
    </li>
    <li>
      <strong>Different Partner re-referral:</strong> If an Organization leaves Chareety and a
      different Partner brings them back, the new Partner receives a fresh 3-month window at the
      25% introductory rate.
    </li>
    <li>
      <strong>Unaffiliated return:</strong> If an Organization leaves and signs up again without
      any Partner referral, no commission is owed to any Partner.
    </li>
  </ul>

  <!-- Section 5: On-Site Setup Fee -->
  <h2 id="section-5">5. On-Site Setup Fee</h2>
  <p>
    <strong>5.1</strong> Partners may perform on-site kiosk device setup for Organizations they refer.
    The on-site setup fee of <strong>$100 per visit</strong> is paid directly to the Partner as a
    service fee for the physical installation and configuration of kiosk devices.
  </p>
  <p>
    <strong>5.2</strong> This fee covers physical kiosk device setup only. It does not apply to
    sales visits, service calls, training sessions, or any other type of on-site visit.
  </p>
  <p>
    <strong>5.3</strong> The on-site setup fee is separate from and not included in commission
    calculations or Platform Revenue.
  </p>

  <!-- Section 6: Payment Terms -->
  <h2 id="section-6">6. Payment Terms</h2>
  <p>
    <strong>6.1</strong> Commissions are calculated monthly on the 1st of each month for the
    previous month's Platform Revenue activity.
  </p>
  <p>
    <strong>6.2</strong> Payment is made via Stripe Connect (ACH bank transfer). There is no
    minimum payout threshold; all earned commissions are paid each billing cycle.
  </p>
  <p>
    <strong>6.3</strong> The Partner must set up a Stripe Connect Express account (individual or
    business) to receive payments. Failure to set up a Stripe Connect account may result in
    delayed payments.
  </p>
  <p>
    <strong>6.4</strong> Partners earning over $600 per calendar year will receive a Form 1099-NEC
    from Stripe, in accordance with IRS reporting requirements. The Partner is solely responsible
    for reporting and paying all applicable taxes on commission income.
  </p>
  <p>
    <strong>6.5</strong> Chareety reserves the right to withhold payment pending investigation
    of suspected fraud, misrepresentation, or other violations of this Agreement. Chareety will
    notify the Partner promptly of any such withholding and will use reasonable efforts to
    complete the investigation within 30 days.
  </p>

  <!-- Section 7: Partner as Support Provider -->
  <h2 id="section-7">7. Partner as Support Provider</h2>
  <p>
    <strong>7.1</strong> Partners who refer Organizations serve as the primary support contact
    for those Organizations. The Partner is expected to provide first-line support, including
    answering questions about platform usage, assisting with basic configuration, and helping
    Organizations resolve common issues.
  </p>
  <p>
    <strong>7.2</strong> For platform-level issues, bugs, or technical problems beyond the
    Partner's scope, the Partner shall escalate to Chareety's support team at
    <strong>partners&#64;chareety.com</strong>.
  </p>
  <p>
    <strong>7.3</strong> Chareety will provide Partners with documentation, training materials,
    and reasonable assistance to enable effective support delivery.
  </p>
  <p>
    <strong>7.4</strong> Partners do not receive separate compensation for providing support
    services. The ongoing lifetime commission structure is designed to compensate Partners for
    both the referral and the ongoing support relationship.
  </p>

  <!-- Section 8: Intellectual Property and Branding -->
  <h2 id="section-8">8. Intellectual Property and Branding</h2>
  <p>
    <strong>8.1</strong> Chareety grants the Partner a limited, non-exclusive, non-transferable,
    revocable license to use Chareety trademarks, logos, and marketing materials solely for the
    purpose of referring Organizations to the Chareety platform.
  </p>
  <p>
    <strong>8.2</strong> The Partner may not modify, alter, or create derivative works from
    Chareety branding or marketing materials without prior written consent from Chareety.
  </p>
  <p>
    <strong>8.3</strong> All intellectual property, including but not limited to trademarks, trade
    names, logos, software, and proprietary technology, remains the exclusive property of
    xoomtag llc.
  </p>
  <p>
    <strong>8.4</strong> The Partner must not make false, misleading, or unauthorized claims about
    the Chareety platform, its features, pricing, or capabilities. All representations to
    prospective Organizations must be truthful and consistent with official Chareety materials.
  </p>

  <!-- Section 9: Non-Compete and Non-Solicitation -->
  <h2 id="section-9">9. Non-Compete and Non-Solicitation</h2>

  <h3>9.1 During Partnership</h3>
  <p>
    While an active Chareety Partner, you shall not actively promote, sell, market, or refer
    nonprofit organizations to competing donation management platforms. "Competing platforms"
    include but are not limited to: Donorbox, Givebutter, Zeffy, Bloomerang, and similar
    donation management SaaS products that provide substantially similar functionality to the
    Chareety platform.
  </p>

  <h3>9.2 After Termination &mdash; Non-Solicitation (2 Years)</h3>
  <p>
    For a period of two (2) years following termination of this Agreement (regardless of the
    reason for termination), the Partner shall not directly or indirectly solicit, encourage,
    induce, or facilitate any Organization that is or was a Chareety customer to migrate to,
    adopt, or evaluate a competing platform. This restriction applies to:
  </p>
  <ul>
    <li>Organizations the Partner directly referred to Chareety;</li>
    <li>Organizations the Partner became aware of through participation in the Chareety Partner program; and</li>
    <li>Organizations with which the Partner had contact in their capacity as a Chareety Partner.</li>
  </ul>

  <h3>9.3 After Termination &mdash; Non-Compete (1 Year)</h3>
  <p>
    For a period of one (1) year following termination of this Agreement, the Partner shall not
    sell, promote, or market competing donation management platforms to prospective nonprofit
    organizations that were in the Partner's referral pipeline (submitted but not yet converted)
    at the time of termination.
  </p>

  <h3>9.4 Enforceability</h3>
  <p>
    If any court of competent jurisdiction determines that any restriction set forth in this
    Section 9 is unenforceable due to its scope, duration, or geographic reach, the restriction
    shall be modified to the minimum extent necessary to render it enforceable, and shall be
    enforced as modified.
  </p>
  <p>
    The Partner acknowledges and agrees that the commission structure set forth in this Agreement,
    including the lifetime commission provision, constitutes adequate and sufficient consideration
    for the restrictions contained in this Section 9.
  </p>

  <!-- Section 10: Independent Contractor -->
  <h2 id="section-10">10. Independent Contractor</h2>
  <p>
    <strong>10.1</strong> The Partner is an independent contractor and is not an employee, agent,
    joint venturer, or legal representative of Chareety. Nothing in this Agreement shall be
    construed to create an employment, agency, or partnership relationship between the parties.
  </p>
  <p>
    <strong>10.2</strong> The Partner is solely responsible for all taxes (including self-employment
    taxes), insurance, licenses, and business expenses incurred in the performance of activities
    under this Agreement.
  </p>
  <p>
    <strong>10.3</strong> The Partner is not entitled to any employee benefits, including but not
    limited to health insurance, retirement benefits, workers' compensation, unemployment insurance,
    or any other employment protections.
  </p>
  <p>
    <strong>10.4</strong> The Partner has no authority to bind Chareety to any contract, obligation,
    or commitment, and shall not represent to any third party that the Partner has such authority.
  </p>

  <!-- Section 11: Representations and Warranties -->
  <h2 id="section-11">11. Representations and Warranties</h2>
  <p>
    <strong>11.1</strong> The Partner represents and warrants that:
  </p>
  <ul>
    <li>The Partner has the legal authority and capacity to enter into this Agreement;</li>
    <li>All information provided during registration and throughout the partnership is accurate, complete, and current;</li>
    <li>The Partner will comply with all applicable federal, state, and local laws and regulations in connection with activities under this Agreement.</li>
  </ul>
  <p>
    <strong>11.2</strong> The Partner further warrants that:
  </p>
  <ul>
    <li>The Partner will not use deceptive, misleading, or unethical practices to obtain referrals or to induce Organizations to join the Chareety platform;</li>
    <li>The Partner will not make unauthorized promises, commitments, or guarantees to prospective Organizations regarding pricing, features, service levels, or any other aspect of the Chareety platform.</li>
  </ul>

  <!-- Section 12: Confidentiality -->
  <h2 id="section-12">12. Confidentiality</h2>
  <p>
    <strong>12.1</strong> During the term of this Agreement and for a period of three (3) years
    following termination, the Partner shall maintain the confidentiality of all non-public
    information received from or through Chareety, including but not limited to:
  </p>
  <ul>
    <li>Commission rates, structures, and payment details;</li>
    <li>Internal platform information, roadmaps, and technical specifications;</li>
    <li>Organization data, including identities, donation volumes, and account details;</li>
    <li>Business strategies, financial information, and operational processes.</li>
  </ul>
  <p>
    <strong>12.2</strong> This confidentiality obligation does not apply to information that:
    (a) is or becomes publicly available through no fault of the Partner; (b) was known to the
    Partner prior to disclosure by Chareety; (c) is independently developed by the Partner
    without use of Chareety's confidential information; or (d) is required to be disclosed by
    law, provided the Partner gives Chareety reasonable prior notice.
  </p>

  <!-- Section 13: Limitation of Liability -->
  <h2 id="section-13">13. Limitation of Liability</h2>
  <p>
    <strong>13.1</strong> To the maximum extent permitted by applicable law, Chareety's total
    aggregate liability to the Partner under this Agreement, whether in contract, tort, or
    otherwise, shall not exceed the total commissions actually paid to the Partner during the
    twelve (12) months immediately preceding the event giving rise to the claim.
  </p>
  <p>
    <strong>13.2</strong> In no event shall Chareety be liable for any indirect, incidental,
    special, consequential, or punitive damages, including but not limited to loss of profits,
    loss of business, or loss of data, regardless of whether Chareety was advised of the
    possibility of such damages.
  </p>
  <p>
    <strong>13.3</strong> The limitations set forth in this Section 13 shall not apply to
    liability arising from fraud or willful misconduct by Chareety.
  </p>

  <!-- Section 14: Indemnification -->
  <h2 id="section-14">14. Indemnification</h2>
  <p>
    The Partner agrees to indemnify, defend, and hold harmless Chareety, xoomtag llc, and their
    respective officers, directors, employees, and agents from and against any and all claims,
    damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees)
    arising out of or relating to:
  </p>
  <ul>
    <li>Any misrepresentation made by the Partner to a prospective Organization or any third party;</li>
    <li>Any violation of applicable laws or regulations by the Partner;</li>
    <li>Any breach of this Agreement by the Partner; or</li>
    <li>Any negligent or wrongful act or omission of the Partner in connection with activities under this Agreement.</li>
  </ul>

  <!-- Section 15: Term -->
  <h2 id="section-15">15. Term</h2>
  <p>
    <strong>15.1</strong> This Agreement becomes effective upon the Partner's acceptance (by
    clicking "I Accept" or by completing Partner registration) and shall continue in effect
    until terminated by either party in accordance with Section 16.
  </p>
  <p>
    <strong>15.2</strong> There is no minimum term or commitment period. Either party may
    terminate this Agreement at any time in accordance with Section 16.
  </p>

  <!-- Section 16: Termination -->
  <h2 id="section-16">16. Termination</h2>
  <p>
    <strong>16.1</strong> Either party may terminate this Agreement for any reason by providing
    thirty (30) days' written notice to the other party via email.
  </p>
  <p>
    <strong>16.2</strong> Chareety may terminate this Agreement immediately, without prior notice,
    if the Partner:
  </p>
  <ul>
    <li>Engages in fraud or fraudulent activity;</li>
    <li>Makes material misrepresentations to Organizations or prospective Organizations;</li>
    <li>Violates the non-compete or non-solicitation provisions set forth in Section 9;</li>
    <li>Engages in illegal activity in connection with the Chareety platform or the Partner's referral activities.</li>
  </ul>
  <p>
    <strong>16.3</strong> Upon termination of this Agreement:
  </p>
  <ul>
    <li>All earned but unpaid commissions will be calculated and paid in the next regular billing cycle;</li>
    <li>Commission accrual ceases at the end of the notice period (or immediately, in the case of termination for cause);</li>
    <li>The Partner must cease all use of Chareety branding, trademarks, and marketing materials within seven (7) days;</li>
    <li>The non-compete and non-solicitation obligations set forth in Sections 9.2 and 9.3 survive termination for the periods specified therein;</li>
    <li>The confidentiality obligations set forth in Section 12 survive termination for three (3) years.</li>
  </ul>

  <!-- Section 17: Commission Forfeiture -->
  <h2 id="section-17">17. Commission Forfeiture</h2>
  <p>
    <strong>17.1</strong> If the Partner is terminated for cause (fraud, misrepresentation, or
    material breach of this Agreement), Chareety reserves the right to forfeit any unpaid
    commissions that have accrued but not yet been paid.
  </p>
  <p>
    <strong>17.2</strong> Commissions are not forfeited in the case of voluntary termination by
    either party or termination without cause. In such cases, all earned commissions will be paid
    in full in the next regular billing cycle following termination.
  </p>

  <!-- Section 18: Governing Law and Disputes -->
  <h2 id="section-18">18. Governing Law and Disputes</h2>
  <p>
    <strong>18.1</strong> This Agreement shall be governed by and construed in accordance with the
    laws of the State of Illinois, without regard to its conflict of laws provisions.
  </p>
  <p>
    <strong>18.2</strong> In the event of any dispute arising out of or relating to this Agreement,
    the parties shall first attempt to resolve the dispute through informal negotiation. The
    initiating party shall provide written notice of the dispute, and the parties shall negotiate
    in good faith for a period of thirty (30) days.
  </p>
  <p>
    <strong>18.3</strong> If the dispute is not resolved through informal negotiation, it shall be
    submitted to binding arbitration administered by the American Arbitration Association (AAA) in
    accordance with its Commercial Arbitration Rules. The arbitration shall take place in DuPage
    County, Illinois.
  </p>
  <p>
    <strong>18.4</strong> The parties agree that any arbitration shall be conducted on an individual
    basis and not as a class, consolidated, or representative action. Neither party may participate
    in a class action or class-wide arbitration related to this Agreement.
  </p>

  <!-- Section 19: Modifications -->
  <h2 id="section-19">19. Modifications</h2>
  <p>
    <strong>19.1</strong> Chareety reserves the right to modify, amend, or update this Agreement
    at any time. Non-financial modifications take effect thirty (30) days after written notice is
    provided to the Partner via email.
  </p>
  <p>
    <strong>19.2</strong> Any changes to commission rates or commission structure require sixty (60)
    days' written notice to the Partner. Commission rate changes apply only to Platform Revenue
    accrued after the effective date of the change.
  </p>
  <p>
    <strong>19.3</strong> The Partner's continued participation in the Chareety Partner program
    after the applicable notice period constitutes acceptance of the modified terms. If the Partner
    does not agree to the modifications, the Partner may terminate this Agreement in accordance
    with Section 16.
  </p>

  <!-- Section 20: General Provisions -->
  <h2 id="section-20">20. General Provisions</h2>
  <p>
    <strong>20.1 Entire Agreement.</strong> This Agreement constitutes the entire agreement between
    the parties with respect to the subject matter hereof and supersedes all prior and contemporaneous
    agreements, understandings, negotiations, and discussions, whether oral or written.
  </p>
  <p>
    <strong>20.2 Severability.</strong> If any provision of this Agreement is held to be invalid,
    illegal, or unenforceable, the remaining provisions shall continue in full force and effect.
    The invalid provision shall be modified to the minimum extent necessary to make it valid and
    enforceable.
  </p>
  <p>
    <strong>20.3 Waiver.</strong> The failure of either party to enforce any provision of this
    Agreement shall not constitute a waiver of that party's right to enforce that provision or
    any other provision in the future.
  </p>
  <p>
    <strong>20.4 Assignment.</strong> The Partner may not assign, transfer, or delegate this
    Agreement or any rights or obligations hereunder without the prior written consent of Chareety.
    Chareety may assign this Agreement without the Partner's consent in connection with a merger,
    acquisition, or sale of all or substantially all of its assets.
  </p>
  <p>
    <strong>20.5 Notices.</strong> All notices required or permitted under this Agreement shall be
    in writing and delivered via email. Notices to Chareety shall be sent to
    <strong>partners&#64;chareety.com</strong>. Notices to the Partner shall be sent to the email
    address on file in the Partner's account.
  </p>
  <p>
    <strong>20.6 Force Majeure.</strong> Neither party shall be liable for any failure or delay in
    performing its obligations under this Agreement due to circumstances beyond its reasonable
    control, including but not limited to acts of God, natural disasters, war, terrorism,
    pandemics, government actions, power failures, internet outages, or other force majeure events.
  </p>

  <!-- Contact Information -->
  <div class="contact-info">
    <h3>Contact Information</h3>
    <p>
      <strong>xoomtag llc, doing business as Chareety</strong><br>
      Bartlett, IL 60103<br>
      Bartlett, IL 60103<br>
      Email: <a href="mailto:partners@chareety.com">partners&#64;chareety.com</a>
    </p>
  </div>

</div>
